import React, { useState } from "react";
import Media from "react-media";
import { ThemeProvider } from "onnit-ui/lib/themes/styled";
import GlobalStyle from "onnit-ui/lib/themes/GlobalStyle";
import Grid from "onnit-ui/lib/module/grid/12/Grid";
import theme from "onnit-ui/lib/themes/light";
import Box from "onnit-ui/lib/box/Box";
import Button from "onnit-ui/lib/button/Button";
import Text, { MultiColumnText } from "onnit-ui/lib/text";
import useOnnitContext from "onnit-ui/lib/hooks/useOnnitContext";
import useScrollToRef from "../../hooks/useScrollToRef";
import usePageData from "../../hooks/usePageData";
import { Img, Picture } from "onnit-ui/lib/image";
import Embed from "onnit-ui/lib/video/Embed";
import Video from "onnit-ui/lib/video/Video";
import HubSpotEmailCapture from "../../components/common/HubSpotEmailCapture";
import UpSell from "../../components/common/UpSell";
import BgImageGradient from "onnit-ui/lib/module/BgImageGradient";
import DefaultProductPageTop from "../../components/common/layouts/DefaultProductPageTop";
import ProductImageGrid from "onnit-ui/lib/module/ProductImageGrid";
import ImageContentPortrait from "onnit-ui/lib/module/ImageContentPortrait";
import LabelContent from "onnit-ui/lib/module/LabelContent";
import MediaCollage02 from "onnit-ui/lib/module/MediaCollage02";
import MediaCollage03 from "onnit-ui/lib/module/MediaCollage03";
import HeadlineLabelContent from "onnit-ui/lib/module/HeadlineLabelContent";
import { Card, Cards } from "onnit-ui/lib/module/Cards";

function Page() {
    const onnitContext = useOnnitContext();
    const { pageData, findProduct } = usePageData();
    const [tryFreeRef, scrollToTryFree] = useScrollToRef();
    const [showFreeWorkout, setShowFreeWorkout] = useState(false);

    if (!pageData || !onnitContext) return null;

    const IMG_URL = onnitContext.url.cdn.static_images + "/onnit-6/durability";
    const SCDN_URL = onnitContext.url.cdn.static + "/images/onnit-6/durability";
    const IMG_PARAMS = "?q=40&fm=pjpg&auto=compress,format";
    const green = "#40611F";

    const steelMaceUpsell = findProduct(199);

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />

                <DefaultProductPageTop>
                    <ProductImageGrid variant="layout2">
                        <Picture
                            lazyLoad={false}
                            width={1888}
                            height={1158}
                            alt="Man using steel mace"
                            src={[
                                `${IMG_URL}/gallery-1-1-mobile.jpg${IMG_PARAMS}`,
                                `${IMG_URL}/gallery-1-1.jpg${IMG_PARAMS}`,
                            ]}
                        />
                        <Picture
                            lazyLoad={false}
                            width={1888}
                            height={848}
                            alt="Onnit 6 Durability Logo"
                            src={[
                                `${IMG_URL}/durability-logo-mobile.png${IMG_PARAMS}`,
                                `${IMG_URL}/durability-logo.png${IMG_PARAMS}`,
                            ]}
                        />
                        <Img
                            lazyLoad={false}
                            alt="Man stretching on ground"
                            src={`${IMG_URL}/gallery-1-2.jpg${IMG_PARAMS}`}
                            width={928}
                            height={932}
                        />
                        <Img
                            lazyLoad={false}
                            alt="Woman using steel mace on yoga mat"
                            src={`${IMG_URL}/gallery-1-3.jpg${IMG_PARAMS}`}
                            width={928}
                            height={932}
                        />
                    </ProductImageGrid>
                </DefaultProductPageTop>

                <div className="hide-ie">
                    <Grid pt={[0, 6, 8]} pb={[6, 6, 8]}>
                        <Box gridColumn={["1 / -1", "1/-1", "2 / span 10"]} px={4}>
                            <Embed
                                title="Onnit 6 Durability product video"
                                src="https://player.vimeo.com/video/424179922"
                            />
                        </Box>
                    </Grid>

                    <LabelContent
                        py={[4, 6, 6, 8]}
                        mb={[4, 6, 6, 8]}
                        label={
                            <Text as="h2" typeStyle="copy05">
                                O6 Durability
                            </Text>
                        }
                        content={
                            <>
                                <Text typeStyle="copyDisplay02" color={green}>
                                    Improve joint mobility and flexibility, and learn to move pain-free, using a
                                    combination of bodyweight and steel club exercises.
                                </Text>
                            </>
                        }
                    />

                    <BgImageGradient bgSrc={`${IMG_URL}/bgImageGradient.jpg${IMG_PARAMS}`} bgColor="black">
                        <Grid display={["block", "block", "grid"]} pt={[6, 6, 6, 11]} pb={[0, 6, 6, 8]} px={4}>
                            <Media
                                query="(min-width: 960px)"
                                render={() => (
                                    <Box gridColumn={["1 / -1", "1 / -1", "2 / span 6"]}>
                                        <Video autoPlay muted loop placeholder={[495, 197]} title="Man doing yoga">
                                            <source src={`${SCDN_URL}/pano.mp4`} type="video/mp4" />
                                        </Video>
                                    </Box>
                                )}
                            />
                            <Box
                                gridColumn={["1 / -1", "1 / -1", "1 / -1", "2 / span 9"]}
                                pt={[6, 6, 6, 8]}
                                pb={[0, 6, 6, 8]}
                            >
                                <Text as="h2" typeStyle="copyDisplay02" color="white" mb={[4, 6, 7]}>
                                    Onnit 6 Durability Benefits
                                </Text>
                                <MultiColumnText>
                                    <Text as="p" typeStyle="copy02" color="white" mb={[4, 4, 6]}>
                                        6 weeks of workouts, consisting of 4 resistance training/conditioning sessions
                                        and 2 recovery-focused days per week.
                                    </Text>
                                    <Text as="p" typeStyle="copy02" color="white" mb={[4, 4, 6]}>
                                        All you need is one pair of 5-lb steel clubs, a foam roller and a ball.
                                    </Text>
                                    <Text as="p" typeStyle="copy02" color="white" mb={[4, 4, 6]}>
                                        Complete video tutorials for more than 100 exercises featured in the program.
                                    </Text>
                                    <Text as="p" typeStyle="copy02" color="white">
                                        3 levels of progression for every exercise, so you can easily customize each
                                        workout based on your ability level. With the videos provided, it will feel like
                                        you’re here at Onnit HQ getting in-person coaching from Chief Fitness Officer
                                        John Wolf.
                                    </Text>
                                </MultiColumnText>
                            </Box>
                        </Grid>
                    </BgImageGradient>

                    <Box bg="black" pt={6} pb={[6, 6, 6, 8]}>
                        <MediaCollage03
                            item1={
                                <Img
                                    src={`${IMG_URL}/collage-1-1.jpg${IMG_PARAMS}`}
                                    alt="Woman doing hip thrusters on yoga mat"
                                    width={1168}
                                    height={646}
                                />
                            }
                            item2={
                                <Img
                                    src={`${IMG_URL}/collage-1-2.jpg${IMG_PARAMS}`}
                                    alt="woman using steel maces"
                                    width={448}
                                    height={448}
                                />
                            }
                            item3={
                                <Img
                                    src={`${IMG_URL}/collage-1-3.jpg${IMG_PARAMS}`}
                                    alt="man doing bicept pose smiling"
                                    width={676}
                                    height={390}
                                />
                            }
                        />
                        <Grid display={["block", "block", "block", "grid"]} px={4} pt={[6, 6, 6, 8]}>
                            <Box gridColumn="2 / span 4">
                                <Text as="p" typeStyle="copy02" color="white" mb={[4, 4, 6]}>
                                    A variety of effective and fun training methods, including supersets, density
                                    circuits, and flows. Bonus: the Game of the Week, a challenge that tests your limits
                                    and sets benchmarks for your progress.
                                </Text>

                                <Text as="p" typeStyle="copy02" color="white" mb={[4, 4, 6]}>
                                    Warmups and cooldowns built into each session—no more pulled muscles because you
                                    didn’t warm up properly!
                                </Text>
                            </Box>
                            <Box gridColumn="7 / span 4">
                                <Text as="p" typeStyle="copy02" color="white" mb={[4, 4, 6]}>
                                    Instruction on proper use of a foam roller and massage ball, to help restore
                                    mobility and tissue quality, and prevent injury. Release your tight neck, shoulders,
                                    back, hips, and more!
                                </Text>
                                <Text as="p" typeStyle="copy02" color="white" mb={[4, 4, 6]}>
                                    Delicious, fat-burning recipes and nutrition tips to optimize your diet.
                                </Text>
                                <Text as="p" typeStyle="copy02" color="white">
                                    Mindset strategies from Onnit Founder Aubrey Marcus.
                                </Text>
                            </Box>
                        </Grid>
                    </Box>

                    <Grid px={4} py={[6, 6, 6, 8]} ref={tryFreeRef}>
                        <Text as="a" id="free-workout-signup" />
                        <Box gridColumn={["1 / -1", "1 / -1", "1 / -1", "1 / span 8"]}>
                            {showFreeWorkout ? (
                                <Embed title="Free Workout Video" src="https://player.vimeo.com/video/406673280" />
                            ) : (
                                <Img
                                    alt="Free workout video preview"
                                    src={`${IMG_URL}/tryfree.jpg${IMG_PARAMS}`}
                                    width={1408}
                                    height={778}
                                />
                            )}
                        </Box>
                        <Box
                            gridColumn={["1 / -1", "1 / -1", "1 / -1", "9 / span 4"]}
                            py={4}
                            px={[0, 0, 0, 6]}
                            alignSelf="center"
                        >
                            <Text
                                as="h3"
                                fontStyle="italic"
                                fontSize={[6, 6, 6, 8]}
                                fontWeight="bold"
                                mb={5}
                                lineHeight={2}
                            >
                                Not Convinced Yet?
                                <br />
                                Try it Free
                            </Text>
                            <Text color="grays.4" mb={4} lineHeight={6}>
                                Enter your email and get access to a full workout from the Onnit 6 DURABILITY Program.
                                Once you submit your email, you will be able to watch the video on this page.
                            </Text>
                            <HubSpotEmailCapture
                                pageName="Onnit 6 Durability"
                                portalId="2252779"
                                formId="29884c96-1f9a-46d5-9b1d-0b48f75f4f0f"
                                buttonLabel="Get access"
                                onSuccess={() => setShowFreeWorkout(true)}
                            />
                        </Box>
                    </Grid>

                    <Media
                        query="(min-width: 960px)"
                        render={() => (
                            <Grid display={["block", "block", "grid"]} py={6} px={4} mt={6}>
                                <Box gridColumn={["1 / -1", "1 / -1", "3 / span 7"]}>
                                    <Video autoPlay muted loop placeholder={[495, 197]} title="Woman doing crunches">
                                        <source src={`${SCDN_URL}/pano2.mp4`} type="video/mp4" />
                                    </Video>
                                </Box>
                            </Grid>
                        )}
                    />

                    <LabelContent
                        py={[0, 6, 6, 8]}
                        label={
                            <Text as="h2" typeStyle="copy05">
                                Who is it for
                            </Text>
                        }
                        content={
                            <>
                                <Text fontWeight="heavy" lineHeight={3} fontSize={[5, 5, 6, 8]} color={green} mb={6}>
                                    Give us six weeks to help you move like a kid again.
                                </Text>
                                <MultiColumnText>
                                    <Text as="p" typeStyle="copy02" mb={[4, 4, 6]} color="grays.5">
                                        Remember what it was like to be a kid? Back when you were in school, whether you
                                        were a star athlete or you barely passed gym class, you could move in all kinds
                                        of ways, effortlessly. Running, jumping, climbing, crawling… you could do it
                                        all—without pain or restriction.
                                    </Text>

                                    <Text as="p" typeStyle="copy02" mb={[4, 4, 6]} fontWeight="bold" color="grays.5">
                                        So what happened?
                                    </Text>

                                    <Text as="p" typeStyle="copy02" mb={[4, 4, 6]} color="grays.5">
                                        You grew up. You stopped moving as much. You got a job where you sit at a desk
                                        all day, and when you’re not sitting there, you’re in your car, or on the couch.
                                        And a sedentary lifestyle, or, at least, one that doesn’t find you using ranges
                                        of motion like you used to, compounds over the years.
                                    </Text>
                                    <Text as="p" typeStyle="copy02" mb={[4, 4, 6]} color="grays.5">
                                        The longer you go letting your movement skills decline, the harder it is to get
                                        them back.
                                    </Text>
                                    <Text as="p" typeStyle="copy02" mb={[4, 4, 6]} color="grays.5">
                                        Maybe you do go to the gym a few days a week, and you’re still in pretty decent
                                        shape, but you can’t do all the exercises you used to. And if you tried, your
                                        shoulder/back/knee would act up.
                                    </Text>
                                    <Text as="p" typeStyle="copy02" color="grays.5">
                                        Or, perhaps you’re still an athlete. Even one who competes at a high level, or a
                                        very fit person who takes his/her training seriously.{" "}
                                        <b>
                                            You feel good overall, but you wouldn’t deny that you’re a little banged up.
                                            Years of hard competition and workouts have taken their toll, and you’ve
                                            racked up a few injuries.
                                        </b>
                                    </Text>
                                </MultiColumnText>
                            </>
                        }
                    />

                    <MediaCollage02
                        item1={
                            <Img
                                src={`${IMG_URL}/collage-2-1.jpg${IMG_PARAMS}`}
                                alt="woman smiling"
                                width={448}
                                height={336}
                            />
                        }
                        item2={
                            <Img
                                src={`${IMG_URL}/collage-2-2.jpg${IMG_PARAMS}`}
                                alt="woman doing yoga"
                                width={688}
                                height={696}
                            />
                        }
                        item3={
                            <Img
                                src={`${IMG_URL}/collage-2-3.jpg${IMG_PARAMS}`}
                                alt="man doing steel mace workout"
                                width={1168}
                                height={878}
                            />
                        }
                    />

                    <LabelContent
                        py={[0, 6, 6, 8]}
                        label={null}
                        content={
                            <>
                                <MultiColumnText>
                                    <Text as="p" typeStyle="copy02" mb={[4, 4, 6]} color="grays.5">
                                        You rest them, but they don’t seem to get much better. The truth is, most
                                        injuries don’t get better by just leaving them alone. Rest is best when you’re
                                        in the acute stages, but to get back to doing the activities you love and
                                        continue them long-term, you have to change the habits that got you hurt in the
                                        first place. In other words…
                                    </Text>

                                    <Text as="p" typeStyle="copy02" mb={[4, 4, 6]} fontWeight="bold" color="grays.5">
                                        You need to move better.
                                    </Text>

                                    <Text as="p" typeStyle="copy02" mb={[4, 4, 6]} color="grays.5">
                                        When you have sound movement mechanics, the right muscles take on the stress of
                                        your activities.
                                    </Text>
                                    <Text as="p" typeStyle="copy02" mb={[4, 4, 6]} color="grays.5">
                                        You spare your joints, improve performance, and add years to your athletic
                                        career, not to mention overall quality of life.
                                    </Text>
                                    <Text as="p" typeStyle="copy02" color="grays.5">
                                        There’s no reason you shouldn’t be able to move in all the ways you have the
                                        capacity for (and that potential is much greater than you think), no matter how
                                        old you are or what your injury history.{" "}
                                        <b>
                                            Onnit 6 Durability can help you reclaim a strong, supple body that can get
                                            you closer to doing everything you want your body to be able to do, and
                                            more.
                                        </b>
                                    </Text>
                                </MultiColumnText>
                            </>
                        }
                    />

                    <Grid display={["block", "block", "block", "grid"]} px={4} py={[6, 6, 6, 8]}>
                        <Box gridColumn="3 / span 7">
                            <Text as="h2" typeStyle="headlineHero02" fontStyle="normal" color={green}>
                                WHAT IT MEANS <br />
                                TO BE DURABLE
                            </Text>
                        </Box>
                    </Grid>

                    <Box bg="#2B3520" py={[6, 6, 6, 8]}>
                        <HeadlineLabelContent
                            label={
                                <Text typeStyle="copy05" color="white">
                                    Onnit 6
                                </Text>
                            }
                            headline={null}
                            content={
                                <>
                                    <Text typeStyle="copy02" color="white" mb={[4, 4, 6]}>
                                        Durability is defined as the ability to withstand wear, pressure, or damage. In
                                        order to understand it in relation to your body, think of an overhead press
                                        exercise.
                                    </Text>
                                    <Text typeStyle="copy02" color="white">
                                        Imagine holding a barbell in both hands, or a kettlebell in one. The exercise
                                        requires you to press the weight overhead to lockout.
                                    </Text>
                                </>
                            }
                        />

                        <ImageContentPortrait
                            py={[6, 6, 6, 8]}
                            reverse={true}
                            image={
                                <Img
                                    src={`${IMG_URL}/image-2.jpg${IMG_PARAMS}`}
                                    alt="man resting"
                                    width={928}
                                    height={926}
                                />
                            }
                            content={
                                <Box display="flex" alignItems="center" height="100%">
                                    <Text typeStyle="copy02" color="white">
                                        If your shoulders and upper back are tight, you may not be able to press the
                                        weight straight up. You’ll find yourself pushing it slightly forward as it
                                        rises, and that hurts your shoulder joints. If your core is weak, you may start
                                        leaning back excessively, over-arching your lower back. That can set you up for
                                        a low-back injury.
                                    </Text>
                                </Box>
                            }
                        />
                        <ImageContentPortrait
                            py={[0, 6]}
                            image={
                                <Img
                                    src={`${IMG_URL}/image-1.jpg${IMG_PARAMS}`}
                                    alt="woman"
                                    width={926}
                                    height={1202}
                                />
                            }
                            content={
                                <Box display="flex" alignItems="center" height="100%">
                                    <Text typeStyle="copy02" color="white">
                                        Now, what if you had the shoulder and thoracic mobility to press your arm
                                        straight overhead, and the core stability to hold your spine in optimal
                                        alignment while you did it? Not only would you avoid getting hurt, your lifting
                                        would be more efficient. You’d get greater activation in the muscles you want to
                                        work, and you’d be stronger as a result.
                                    </Text>
                                </Box>
                            }
                        />

                        <Grid display={["block", "block", "block", "grid"]}>
                            <Box px={4} py={[0, 6, 6, 8]} gridColumn="4 / span 7">
                                <Text typeStyle="copyHero01" color="white" mb={[4, 4, 6]}>
                                    In short, by improving your ability to move—and control your body in that
                                    movement—you’ll increase your resistance to injury, boost overall performance, and
                                    improve longevity, whether you’re an active person or a dedicated athlete.
                                </Text>
                                <Text typeStyle="copyHero01" color="white">
                                    You will become DURABLE.
                                </Text>
                            </Box>
                        </Grid>
                    </Box>

                    <LabelContent
                        py={[6, 6, 6, 8]}
                        label={<Text typeStyle="copy05">Onnit 6</Text>}
                        content={
                            <>
                                <Text
                                    as="h2"
                                    fontWeight="heavy"
                                    lineHeight={3}
                                    fontSize={[5, 5, 6, 8]}
                                    color={green}
                                    mb={6}
                                >
                                    Onnit 6 Durability is not:
                                </Text>
                                <MultiColumnText>
                                    <Text as="p" typeStyle="copy02" mb={[4, 4, 4, 6]} fontWeight="bold" color="grays.5">
                                        {" "}
                                        An easy stretching program
                                    </Text>

                                    <Text as="p" typeStyle="copy02" mb={[4, 4, 4, 6]} fontWeight="bold" color="grays.5">
                                        An injury-specific rehab program
                                    </Text>

                                    <Text as="p" typeStyle="copy02" mb={[4, 4, 4, 6]} fontWeight="bold" color="grays.5">
                                        A shortcut to big muscles and ripped abs
                                    </Text>

                                    <Text as="p" typeStyle="copy02" mb={[4, 4, 4, 6]} color="grays.5">
                                        Durability isn’t like other fitness programs. The aim isn’t to lift a certain
                                        amount of weight or transform your physique by the end of it. It also isn’t your
                                        mom’s yoga class, or a doctor-prescribed treatment plan. Rather, its purpose is
                                        to make you a better mover, laying the foundation for you to achieve any fitness
                                        goal you may have more easily in the future.
                                    </Text>
                                    <Text as="p" typeStyle="copy02" mb={[4, 4, 4, 6]} color="grays.5">
                                        Using your own bodyweight, along with the steel club, you’ll perform uniquely
                                        challenging resistance exercises that help you develop the positional awareness,
                                        body control, and mobility to accelerate progress in any area of fitness you
                                        like, and maintain your tissue and joint health for many more years.
                                    </Text>

                                    <Text as="p" typeStyle="copy02" color="grays.5">
                                        The Durability workouts are meant to last you well beyond six weeks. After you
                                        complete the program, you can plug any workout you like best into any other
                                        training you do, customizing your routine for greater results… for life.
                                    </Text>
                                </MultiColumnText>
                            </>
                        }
                    />

                    <BgImageGradient
                        bgSrc={`${IMG_URL}/bgImage-2.jpg${IMG_PARAMS}`}
                        bgColor="black"
                        bgProps={{ backgroundPosition: "top right" }}
                    >
                        <Grid display={["block", "block", "grid"]} pt={[10, 10, 11]} pb={[0, 6, 8]} px={4}>
                            <Box gridColumn={["1 / -1", "1 / -1", "2 / span 9"]} py={[6, 6, 6, 8]}>
                                <Text
                                    as="h2"
                                    typeStyle="copyDisplay02"
                                    color="white"
                                    mb={[4, 6, 7]}
                                    fontSize={[8, 8, 8, 8, 10, 10, 12]}
                                >
                                    How is Onnit 6 Durability Different from the other Onnit 6 Programs?
                                </Text>
                                <MultiColumnText>
                                    <Text as="p" typeStyle="copy02" color="white" mb={[4, 4, 6]}>
                                        Onnit 6 Durability is, to date, the most unique of the five offerings in the
                                        Onnit 6 series. While greater mobility and movement skills are ancillary
                                        benefits of all the other Onnit 6 programs, they’re more focused on strength,
                                        power, and overall stability. Durability, on the other hand, zeroes in on the
                                        most important first step in achieving any goal—learning to move properly and
                                        pain-free.
                                    </Text>
                                    <Text as="p" typeStyle="copy02" color="white" mb={[4, 4, 6]}>
                                        Our advice: if you’re looking for a program that helps you get back into shape
                                        after an injury or layoff, or you’re new to Onnit and our brand of training,
                                        start with Onnit 6 Durability. Once you’re moving well and pain-free again,
                                        choose another Onnit 6 program that best suits your next goal.
                                    </Text>
                                </MultiColumnText>
                            </Box>
                        </Grid>
                    </BgImageGradient>

                    <Box bg="#2B3520" py={[6, 6, 6, 8]}>
                        <MediaCollage03
                            pt={[4, 6, 8]}
                            item1={
                                <Img
                                    src={`${IMG_URL}/collage-3-2.jpg${IMG_PARAMS}`}
                                    alt="woman using two steel maces"
                                    width={1168}
                                    height={646}
                                />
                            }
                            item2={
                                <Img
                                    src={`${IMG_URL}/collage-3-1.jpg${IMG_PARAMS}`}
                                    alt="working out with two steel maces"
                                    width={448}
                                    height={448}
                                />
                            }
                            item3={
                                <Img
                                    src={`${IMG_URL}/collage-3-3.jpg${IMG_PARAMS}`}
                                    alt="man working out with two steel maces"
                                    width={676}
                                    height={390}
                                />
                            }
                        />

                        <HeadlineLabelContent
                            py={[0, 6, 8]}
                            label={
                                <Text typeStyle="copy05" color="white">
                                    Onnit 6
                                </Text>
                            }
                            headline={
                                <Text as="h2" typeStyle="headlineHero02" fontStyle="normal" color="white">
                                    Join the club
                                </Text>
                            }
                            content={
                                <>
                                    <Text typeStyle="copy02" color="white" mb={[4, 4, 4, 6]}>
                                        Onnit 6 Durability uses both bodyweight and steel club exercises to enhance
                                        movement skills. Here’s why the club is the perfect accompaniment.
                                    </Text>
                                    <Text typeStyle="copy02" color="white" mb={[4, 4, 4, 6]}>
                                        The offset load and long lever make the weight more difficult to control,
                                        especially in positions where you’re already weak.
                                        <br />
                                        <b>The result: you’ll build greater stability and body control.</b>
                                    </Text>
                                    <Text typeStyle="copy02" color="white">
                                        The club is often lifted with a swinging pendulum motion. Using the club like
                                        this tractions out your joints, decompressing and releasing tissues that get
                                        tight and short from conventional training and daily activities. <br />
                                        <b>The result: you’ll help reduce joint pain and restore flexibility.</b>
                                    </Text>
                                </>
                            }
                        />

                        <ImageContentPortrait
                            py={[6, 6, 6, 8]}
                            reverse={true}
                            image={
                                <Img
                                    src={`${IMG_URL}/image-5.1.jpg${IMG_PARAMS}`}
                                    alt="back of onnit t-shirt"
                                    width={926}
                                    height={1202}
                                />
                            }
                            content={
                                <Box display="flex" alignItems="center" height="100%">
                                    <Text typeStyle="copy02" color="white">
                                        The club is light enough to use for several exercises in succession, but
                                        provides enough load to encourage functional flexibility, similar to partner
                                        stretching. Ever do a straddle stretch while a friend gently pushed on your back
                                        to ease you in deeper? The weight of the club provides a safe loaded stretch
                                        that opens up your shoulders when you lower it behind your head, and your hips
                                        when you swing it between your knees.
                                        <br />
                                        <b>The result: you’ll gain greater ranges of motion.</b>
                                    </Text>
                                </Box>
                            }
                        />
                        <ImageContentPortrait
                            image={
                                <Img
                                    src={`${IMG_URL}/image-6.jpg${IMG_PARAMS}`}
                                    alt="woman doing yoga with steel mace"
                                    width={928}
                                    height={926}
                                />
                            }
                            content={
                                <Box display="flex" alignItems="center" height="100%">
                                    <Text typeStyle="copy02" color="white">
                                        Using light clubs in the way they’re presented in this program will help you
                                        master other offset leverage tools, like the kettlebell and steel mace.
                                        <br />
                                        <b>
                                            The result: you’ll become a stronger, smoother lifter when using any
                                            training implement.
                                        </b>
                                    </Text>
                                </Box>
                            }
                        />
                    </Box>

                    {steelMaceUpsell && (
                        <UpSell
                            textColor="black"
                            buttonColor="black"
                            buttonTextColor="white"
                            pid={199}
                            inStock={steelMaceUpsell.in_stock}
                            price={steelMaceUpsell.price}
                            msrp={steelMaceUpsell.price_msrp}
                            showPercentageSaved={true}
                            img={
                                <Img
                                    src={`${IMG_URL}/splitImage.jpg${IMG_PARAMS}`}
                                    alt="Onnit 6 app and woman using 5 lb Steel Clubs"
                                    width={928}
                                    height={928}
                                />
                            }
                            title={<>5 lb Steel Clubs</>}
                            headingLevel="h3"
                            subtext="5 lb Steel Club Pair"
                            description={<>Want to get started? Add a pair of 5-lb Steel Clubs to your cart today.</>}
                        />
                    )}

                    <Box py={[6, 6, 6, 8]}>
                        <Grid>
                            <Box gridColumn="1 / -1" px={4}>
                                <Text as="h2" typeStyle="copyDisplay02" mb={4} fontSize={[8, 8, 8, 8, 10, 10, 12]}>
                                    Bonus Content
                                </Text>
                            </Box>
                        </Grid>

                        <Cards px={4}>
                            <Card bgSrc={`${onnitContext.url.cdn.static_images}/onnit-6/barbell/recipes.jpg?q=40`}>
                                <Text
                                    textTransform="uppercase"
                                    fontWeight="heavy"
                                    fontSize={[4, 6, 4, 6]}
                                    color="white"
                                >
                                    Recipes
                                </Text>
                            </Card>
                            <Card bgSrc={`${onnitContext.url.cdn.static_images}/onnit-6/barbell/lifestyle.jpg?q=40`}>
                                <Text
                                    textTransform="uppercase"
                                    fontWeight="heavy"
                                    fontSize={[4, 6, 4, 6]}
                                    color="white"
                                >
                                    Biohacks &amp; Lifestyle Tips
                                </Text>
                            </Card>
                            <Card bgSrc={`${onnitContext.url.cdn.static_images}/onnit-6/barbell/mornings.jpg?q=40`}>
                                <Text
                                    textTransform="uppercase"
                                    fontWeight="heavy"
                                    fontSize={[4, 6, 4, 6]}
                                    color="white"
                                >
                                    How to start your mornings
                                </Text>
                            </Card>
                        </Cards>
                    </Box>

                    <ImageContentPortrait
                        py={[6, 6, 6, 8]}
                        image={
                            <Img
                                alt="Portrait of John Wolf"
                                src={`${onnitContext.url.cdn.static_images}/onnit-6/barbell/wolf.jpg?q=40`}
                                placeholder={[930, 1200]}
                            />
                        }
                        content={
                            <Box display="flex" flexDirection="column" height="100%" justifyContent="center">
                                <Text color={green} typeStyle="copyDisplay02" mb={2}>
                                    John Wolf
                                </Text>
                                <Text
                                    fontSize={[1, 1, 2, 2, 4]}
                                    fontStyle="italic"
                                    fontWeight="bold"
                                    textTransform="uppercase"
                                    color="grays.5"
                                    mb={6}
                                >
                                    HEAD COACH, ONNIT 6<br />
                                    CHIEF FITNESS OFFICER, ONNIT
                                </Text>
                                <Text as="p" typeStyle="copy02">
                                    Before becoming Chief Fitness Officer for Onnit, John Wolf founded and owned Wolf
                                    Fitness Systems, a well-known unconventional gym in California that focused on the
                                    integration of various movement theories with unique tools such as the kettlebell,
                                    steel mace, and steel club. He also spent several years as the US Director of
                                    Operations for another world-famous unconventional fitness organization.
                                </Text>
                            </Box>
                        }
                    />
                    <Box display="flex" p={4} pb={[6, 6, 8]}>
                        <Button mx="auto" color={green} onClick={() => scrollToTryFree()} width={[1, "auto"]}>
                            TRY FOR FREE
                        </Button>
                    </Box>
                </div>
            </>
        </ThemeProvider>
    );
}

export default Page;
